<template>
    <main role="main" class="fc-v2">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('menu.tickets.create') || 'Créer un ticket' }}</div>
        </portal>

        <div v-if="loading" class="fc-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <template v-else>
            <header class="fc-hero">
                <div class="fc-hero-icon">
                    <i class="fal fa-clipboard-list"></i>
                </div>
                <h1 class="fc-hero-title">{{ $t('form_choice.title') }}</h1>
                <p class="fc-hero-sub">{{ $t('form_choice.subtitle') }}</p>
            </header>

            <div v-if="!forms || forms.length === 0" class="fc-empty">
                <i class="fal fa-inbox"></i>
                <p>{{ $t('form_choice.empty') }}</p>
            </div>

            <div v-else class="fc-types">
                <section
                    v-for="type in types"
                    :key="type.id"
                    class="fc-type-section"
                >
                    <header v-if="types.length > 1" class="fc-type-header">
                        <span class="fc-type-badge">
                            <i class="fal fa-layer-group"></i>
                        </span>
                        <h2 class="fc-type-name">{{ type.name }}</h2>
                        <span class="fc-type-count">{{ getFormsByType(type.id).length }}</span>
                    </header>

                    <div class="fc-forms-grid">
                        <router-link
                            v-for="form in getFormsByType(type.id)"
                            :key="form.id"
                            :to="`/post/create/forms/${hashId(form.id)}/wizard`"
                            class="fc-form-card"
                        >
                            <span class="fc-form-icon">
                                <i class="fal fa-file-alt"></i>
                            </span>
                            <div class="fc-form-body">
                                <h3 class="fc-form-name">{{ form.name }}</h3>
                                <p v-if="form.description" class="fc-form-desc">
                                    {{ stripHtml(form.description) }}
                                </p>
                            </div>
                            <i class="fal fa-arrow-right fc-form-arrow"></i>
                        </router-link>
                    </div>
                </section>
            </div>
        </template>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { hashId } from '@/helpers/hashId'

export default {
    name: 'FormChoice',
    data() {
        return {
            loading: true
        }
    },
    computed: {
        ...mapState('form', {
            forms: (state) => state.forms,
            config: (state) => state.formsConfig,
            types: (state) => state.types
        })
    },
    methods: {
        ...mapActions('form', ['getForms']),
        hashId,
        getFormsByType(typeId) {
            if (!this.forms) return []
            return this.forms.filter((f) => f.form_type_id === typeId)
        },
        stripHtml(html) {
            if (!html) return ''
            const tmp = document.createElement('div')
            tmp.innerHTML = html
            const text = tmp.textContent || tmp.innerText || ''
            return text.length > 120 ? `${text.substring(0, 120)}...` : text
        }
    },
    created() {
        this.config.filters['active'] = true
        this.getForms()
            .then(() => {
                if (this.forms && this.forms.length === 1) {
                    this.$router.push(`/post/create/forms/${hashId(this.forms[0].id)}/wizard`)
                }
                this.loading = false
            })
            .catch(() => {
            })
    }
}
</script>

<style scoped>
.fc-v2 {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
    padding: 32px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.fc-loading {
    padding: var(--v2-space-6);
    display: flex;
    justify-content: center;
    color: var(--v2-text-3);
    font-size: 1.5rem;
}

.fc-hero {
    text-align: center;
    margin-bottom: var(--v2-space-6);
}

.fc-hero-icon {
    width: 56px;
    height: 56px;
    border-radius: var(--v2-radius-lg);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 1.35rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px;
}

.fc-hero-title {
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    margin: 0 0 6px;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.fc-hero-sub {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    margin: 0;
    line-height: 1.5;
}

.fc-types {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-5);
}

.fc-type-section {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
}

.fc-type-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 2px;
}

.fc-type-badge {
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.78rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.fc-type-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    margin: 0;
    flex: 1;
    letter-spacing: -0.005em;
}

.fc-type-count {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    background: var(--v2-surface-2);
    padding: 2px 10px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
}

.fc-forms-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.fc-form-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    text-decoration: none !important;
    color: inherit !important;
    transition: all var(--v2-transition-fast);
}

.fc-form-card:hover {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
    transform: translateY(-1px);
    box-shadow: var(--v2-shadow-sm);
}

.fc-form-icon {
    width: 42px;
    height: 42px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-size: 1.05rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all var(--v2-transition-fast);
}

.fc-form-card:hover .fc-form-icon {
    background: var(--v2-primary);
    color: #fff;
}

.fc-form-card:hover .fc-form-icon i {
    color: #fff !important;
}

.fc-form-body {
    flex: 1;
    min-width: 0;
}

.fc-form-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    margin: 0 0 3px;
    letter-spacing: -0.005em;
}

.fc-form-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin: 0;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fc-form-arrow {
    color: var(--v2-text-4);
    font-size: 0.85rem;
    flex-shrink: 0;
    transition: all var(--v2-transition-fast);
}

.fc-form-card:hover .fc-form-arrow {
    color: var(--v2-primary);
    transform: translateX(3px);
}

.fc-empty {
    text-align: center;
    padding: var(--v2-space-6) var(--v2-space-4);
    color: var(--v2-text-4);
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.fc-empty i {
    font-size: 2rem;
    display: block;
    margin-bottom: 12px;
    color: var(--v2-text-4);
}

.fc-empty p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

@media (max-width: 640px) {
    .fc-v2 {
        padding: 20px 18px 32px;
    }

    .fc-hero-title {
        font-size: 1.25rem;
    }

    .fc-form-card {
        padding: 14px 16px;
        gap: 12px;
    }

    .fc-form-icon {
        width: 38px;
        height: 38px;
    }
}
</style>
